/**
 * BooleanPrint - Figma-matching print renderer for boolean (O/X) quizzes
 * Layout: PrintQuizTitle → image → O/X icon boxes (56x56, gap-20px)
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { BooleanQuizData } from '../../../types/legacyQuiz'
import { PrintQuizTitle, PrintQuizProblem, PrintQuizImage, PrintInputBox, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: BooleanQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function BooleanPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const correctIsTrue = correctAnswers?.[0] != null
    ? correctAnswers[0] === 'true'
    : quiz.pairs?.left?.[0]?.key === 'true'

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
      />

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* O/X icon boxes - Figma: 56x56 rounded boxes, gap-20px */}
      <div className="flex items-center justify-center gap-[20px] w-full">
        {/* O (blue circle) */}
        <PrintInputBox
          size="md"
          compact={compact}
          className={cn(
            showCorrectAnswers && correctIsTrue
              ? 'border-[#e50000] border-2'
              : ''
          )}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle
              cx="14"
              cy="14"
              r="10"
              stroke={showCorrectAnswers && correctIsTrue ? '#e50000' : '#00AAFF'}
              strokeWidth="4"
              fill="none"
            />
          </svg>
        </PrintInputBox>
        {/* X (red cross) */}
        <PrintInputBox
          size="md"
          compact={compact}
          className={cn(
            showCorrectAnswers && !correctIsTrue
              ? 'border-[#e50000] border-2'
              : ''
          )}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M4 4L20 20M20 4L4 20" stroke="#E50000" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </PrintInputBox>
      </div>
    </div>
  )
}
